/**
 * Manager-queue row helpers — anomaly chip selection (from HR-managed
 * pattern detection at /hr/pattern-detection) and thin-day overlap
 * derivation (from the dashboard's week-triage payload).
 *
 * Single source of truth for what a queue row's two chips can say.
 * The row component never paraphrases a pattern or recomputes pressure.
 */

import type {
  CachedEmployeePatterns,
  CachedTeamPatternsResponse,
  LeaveTypeId,
  PatternKind,
} from '@lms/shared-types'

export type ChipSeverity = 'low' | 'medium' | 'high'

export interface AnomalyChip {
  label: string
  severity: ChipSeverity
  kind: PatternKind
}

/**
 * `no_leave_in_window` is the one detector that's a *positive* signal
 * (HR perf-review hook for under-utilised entitlement). It still flows
 * through the cached endpoint so HR can see it on /hr/pattern-detection,
 * but the manager-queue row chip excludes it regardless of severity or
 * relevance — a manager approving an annual leave doesn't need to be
 * told the employee hasn't taken time off lately. Hard exclusion lives
 * on the FE because changing this for one surface (the row) shouldn't
 * require touching HR's catalog config.
 */
const ROW_EXCLUDED_KINDS: ReadonlySet<PatternKind> = new Set(['no_leave_in_window'])

const SEVERITY_RANK: Record<ChipSeverity, number> = { low: 0, medium: 1, high: 2 }

/** Sentinel in `relevantKinds` meaning "show only when leave.isEmergency". */
const EMERGENCY_SENTINEL = 'emergency_flag'

export interface LeaveContext {
  /** The leave's request type, e.g. "medical", "annual". */
  type: LeaveTypeId
  /** Whether the request carried the emergency flag. */
  isEmergency: boolean
}

/**
 * True when this pattern should appear as a row chip for this leave.
 * Rule, ratified with Hafiz on 2026-05-20:
 *
 * - `relevantKinds == []` → wildcard, always relevant
 * - `relevantKinds` contains the leave's type → relevant
 * - `relevantKinds` contains "emergency_flag" → relevant iff the leave
 *   is flagged as emergency
 *
 * The relevance gate is data-driven (ai.pattern_catalog) so HR can tune
 * which detectors surface on which request types without a deploy.
 */
function isRelevantToLeave(
  relevantKinds: ReadonlyArray<string>,
  leave: LeaveContext,
): boolean {
  if (relevantKinds.length === 0) return true
  if (relevantKinds.includes(leave.type)) return true
  if (leave.isEmergency && relevantKinds.includes(EMERGENCY_SENTINEL)) return true
  return false
}

/**
 * Returns the top anomaly chip for this leave row, or null if none
 * qualify. Picks the highest-severity *relevant* pattern; ties broken
 * by the order the server returned them (catalog order is stable).
 *
 * The chip text is the fact, not the label — "4 sick days in 90d", not
 * "Frequent sick leave". This is Hafiz's "tell me the number, I'll do
 * the verdict" rule. The noun ("sick days") comes from the catalog
 * (`evidenceUnit`); the FE never owns the wording.
 *
 * Source: cached HR-managed pattern detection (ai.leave_patterns), read
 * via /api/v1/leaves/patterns/team. No recomputation here — manager
 * dashboard never triggers pandas.
 */
export function pickTopAnomaly(
  employeeId: string,
  leave: LeaveContext,
  response: CachedTeamPatternsResponse | undefined,
): AnomalyChip | null {
  if (!response) return null
  const employee = response.items.find(
    (item): item is CachedEmployeePatterns => item.employeeId === employeeId,
  )
  if (!employee) return null

  let best: AnomalyChip | null = null
  for (const pattern of employee.patterns) {
    if (ROW_EXCLUDED_KINDS.has(pattern.kind)) continue
    if (!isRelevantToLeave(pattern.relevantKinds, leave)) continue
    const label = `${pattern.evidence.count} ${pattern.evidenceUnit} in ${pattern.evidence.windowDays}d`
    if (best === null || SEVERITY_RANK[pattern.severity] > SEVERITY_RANK[best.severity]) {
      best = { label, severity: pattern.severity, kind: pattern.kind }
    }
  }
  return best
}

// ── Tight-day overlap ────────────────────────────────────────────────────────

export interface TightDayChip {
  /** Short label rendered on the row, e.g. "Tight: Thu". */
  label: string
  /** ISO date of the worst (most pressured) overlapping day. */
  date: string
}

/**
 * Day shape we depend on — mirrors WeekTriageDay from
 * @lms/shared-types/dashboard but kept structural here so the helper is
 * cheap to test without pulling the full type web.
 */
export interface TightDayInput {
  date: string
  isWeekend: boolean
  isHoliday: boolean
  slotsTotal: number
  onLeaveApproved: number
  onLeavePending: number
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function shortWeekday(iso: string): string {
  // Parse YYYY-MM-DD without TZ shenanigans — the day-of-week is local
  // to the date string itself, not the runtime's timezone.
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return ''
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return WEEKDAY_LABELS[dow] ?? ''
}

function isoBetween(target: string, start: string, end: string): boolean {
  return target >= start && target <= end
}

/**
 * "Decide today" classifier — Hafiz's dumb rule, ratified 2026-05-20:
 *
 *   submitted ≥3 working days ago, OR start date is within 2 working days.
 *
 * Intentionally NOT gated on the policy registry / SLA table. When that
 * lands later the rule can be replaced with the policy value without
 * any FE change — the input shape is still `submittedAt` + `startDate`.
 *
 * Weekend-aware (Sat/Sun are skipped when counting working days);
 * holiday-aware is deferred until the FE has the holiday list cheap —
 * the false-positive cost on a holiday is "we mark something as
 * decide-today one day early", which Hafiz prefers over the false-
 * negative cost of "we hide something urgent because the threshold
 * happened to land on a holiday".
 */
export interface DecideTodayInput {
  /** ISO datetime — the leave's submittedAt / createdAt. */
  submittedAt: string
  /** ISO date — the leave's start date. */
  startDate: string
}

function isoDateOnly(iso: string): string {
  // Accepts ISO datetime or date and returns the YYYY-MM-DD prefix.
  return iso.slice(0, 10)
}

function dateFromIso(iso: string): Date {
  const [y, m, d] = isoDateOnly(iso).split('-').map(Number)
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1))
}

/**
 * Working-day count from `from` to `to` (inclusive). Returns 0 if to < from.
 * Weekend-aware only — see comment on isDecideToday for why holidays are
 * intentionally not counted here.
 */
function workingDaysBetween(from: Date, to: Date): number {
  if (to < from) return 0
  let n = 0
  const cur = new Date(from)
  while (cur <= to) {
    const dow = cur.getUTCDay()
    if (dow !== 0 && dow !== 6) n += 1
    cur.setUTCDate(cur.getUTCDate() + 1)
  }
  return n
}

/** Working days strictly after `submitted` through `today` inclusive.
 *  Submitting on a weekend "starts the clock" on the next working day —
 *  this avoids penalising the helper for paperwork filed off-hours. */
function workingDaysElapsedSince(submitted: Date, today: Date): number {
  if (today <= submitted) return 0
  const dayAfter = new Date(submitted)
  dayAfter.setUTCDate(dayAfter.getUTCDate() + 1)
  return workingDaysBetween(dayAfter, today)
}

export function isDecideToday(leave: DecideTodayInput, today: Date): boolean {
  const todayIso = today.toISOString().slice(0, 10)
  const todayUtc = dateFromIso(todayIso)
  const submittedUtc = dateFromIso(leave.submittedAt)
  const startUtc = dateFromIso(leave.startDate)

  // Submitted ≥3 working days ago.
  if (workingDaysElapsedSince(submittedUtc, todayUtc) >= 3) return true

  // Starts within 2 working days from today (0/1/2 working days away).
  if (startUtc >= todayUtc) {
    const daysUntilStart = workingDaysBetween(todayUtc, startUtc) - 1
    if (daysUntilStart <= 2) return true
  }
  return false
}

/**
 * Returns the single worst tight day overlapping the leave's date range,
 * or null if none. "Tight" = working day where, if every overlapping
 * pending request landed approved, the team would breach its slot
 * capacity (slotsTotal - onLeaveApproved - onLeavePending <= 0) or be
 * down to its last slot (=1). Matches the existing `tightDays` schema
 * vocabulary on DashboardTeam — one word, repo-wide.
 *
 * Weekend and holiday days are excluded — they aren't slot-bearing in
 * the coverage policy and surface as visual gaps on the strip.
 *
 * Picks one day max — Maya's one-chip-per-row rule.
 */
export function pickWorstTightDay(
  startDate: string,
  endDate: string,
  days: ReadonlyArray<TightDayInput>,
): TightDayChip | null {
  let worst: TightDayInput | null = null
  let worstSlack = Number.POSITIVE_INFINITY
  for (const day of days) {
    if (day.isWeekend || day.isHoliday) continue
    if (!isoBetween(day.date, startDate, endDate)) continue
    const slack = day.slotsTotal - day.onLeaveApproved - day.onLeavePending
    if (slack > 1) continue
    if (slack < worstSlack) {
      worst = day
      worstSlack = slack
    }
  }
  if (!worst) return null
  return {
    label: `Tight: ${shortWeekday(worst.date)}`,
    date: worst.date,
  }
}

// ── Wave-3 placeholder shapes (server-computed; wired in a later slice) ──────
// These types are defined here so pickTopBadge has a stable signature today.
// The fields are not yet populated on LeaveListItem — wave 3 adds them.

export interface BlackoutProximityChip {
  /** Plain prose, e.g. "Ends 2d before Q3 freeze". */
  label: string
  daysUntil: number
}

export interface ClusterImpactChip {
  /** Plain prose, e.g. "Would make Thu 3/3". */
  label: string
  date: string
}

export interface HolidayAdjacencyChip {
  /** Plain prose, e.g. "Straddles Hari Raya cluster". */
  label: string
  holidayName: string
}

// ── Single-token row badge selector ──────────────────────────────────────────

export type RowBadgeKind =
  | 'emergency'
  | 'blackout'
  | 'cluster'
  | 'tight'
  | 'holiday'
  | 'anomaly'

export interface RowBadge {
  kind: RowBadgeKind
  /** Maps to <Severity kind={...}> — visual loudness. */
  severity: 'high' | 'medium' | 'info'
  label: string
}

export interface PickTopBadgeInputs {
  isEmergency: boolean
  blackout: BlackoutProximityChip | null
  cluster: ClusterImpactChip | null
  tightDay: TightDayChip | null
  holiday: HolidayAdjacencyChip | null
  anomaly: AnomalyChip | null
}

const ANOMALY_SEVERITY_TO_BADGE: Record<ChipSeverity, RowBadge['severity']> = {
  high: 'high',
  medium: 'medium',
  low: 'info',
}

/**
 * Maya's rule: exactly one loud token per row. Priority chain ratified
 * with Hafiz 2026-05-20:
 *
 *   emergency > blackout > cluster > tight > holiday > anomaly
 *
 * First match wins; everything else suppresses. No tooltip on the row
 * carries the runner-up — the detail page is for the long story.
 *
 * Wave 1 ships with blackout/cluster/holiday inputs always null; the
 * server fields land in wave 3 and the same selector keeps working
 * without further changes.
 */
export function pickTopBadge(inputs: PickTopBadgeInputs): RowBadge | null {
  if (inputs.isEmergency) {
    return { kind: 'emergency', severity: 'high', label: 'Emergency' }
  }
  if (inputs.blackout) {
    return { kind: 'blackout', severity: 'high', label: inputs.blackout.label }
  }
  if (inputs.cluster) {
    return { kind: 'cluster', severity: 'high', label: inputs.cluster.label }
  }
  if (inputs.tightDay) {
    return { kind: 'tight', severity: 'medium', label: inputs.tightDay.label }
  }
  if (inputs.holiday) {
    return { kind: 'holiday', severity: 'medium', label: inputs.holiday.label }
  }
  if (inputs.anomaly) {
    return {
      kind: 'anomaly',
      severity: ANOMALY_SEVERITY_TO_BADGE[inputs.anomaly.severity],
      label: inputs.anomaly.label,
    }
  }
  return null
}
